import React from "react";
import LegalLayout from "./LegalLayout";
import { LINKS } from "../../lib/constants";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="July 2026">
      <h2>1. Introduction</h2>
      <p>
        This privacy notice tells you how we collect and process your personal data when you use
        the ProfitMIND website (<strong>profitmind.co.uk</strong>), our advisory services and
        booking pages, or our sister product <strong>forecastly</strong> (the cashflow forecasting
        software). forecastly is a trading name of ProfitMIND Ltd. By using our services, you
        confirm you are over 18 years of age and entering into the agreement on behalf of yourself
        or your business.
      </p>
      <p>
        "ProfitMIND Ltd" is the data controller and is responsible for your personal data (referred
        to as "we", "us" or "our" in this notice).
      </p>

      <h3>Contact details</h3>
      <p>
        Legal entity: ProfitMIND Ltd<br />
        Trading as: forecastly<br />
        Postal address: 4 Capricorn Centre, Cranes Farm Road, Basildon, Essex, SS14 3JJ, United Kingdom<br />
        Email: <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a><br />
        Registered in England and Wales. Reg. no. 09918255
      </p>
      <p>
        It is important that the information we hold about you is accurate and current. Please
        update us by email or in your account settings if anything changes.
      </p>

      <h2>2. What data we collect, why, and on what lawful basis</h2>
      <p>Personal data means any information that identifies an individual. We may process the following categories about you:</p>

      <h3>Enquiry &amp; booking data (ProfitMIND services)</h3>
      <p>
        Name, email, phone number, business name, revenue band, and anything you choose to share in
        the website contact form, the TidyCal booking flow, or the Profit &amp; Cashflow Clarity Check self-assessment.<br />
        <em>Purpose:</em> to respond to your enquiry, deliver any session or programme you've booked,
        and send your Clarity Check report.<br />
        <em>Lawful basis:</em> performance of a contract, or our legitimate interest in responding to enquiries.
      </p>

      <h3>Account data (forecastly)</h3>
      <p>
        Your name, email address, password (stored only as a one-way bcrypt hash — we never see it),
        business name, and chosen pricing plan.<br />
        <em>Purpose:</em> to create and run your account. <em>Lawful basis:</em> performance of a contract.
      </p>

      <h3>Financial data you enter (forecastly)</h3>
      <p>
        Your business income, costs, opening cash, personal pay, monthly actuals, goals, and any
        notes you choose to add. This is YOUR business data; we treat it as confidential.<br />
        <em>Purpose:</em> to compute your forecast, variance and AI insights, and display them back
        to you. <em>Lawful basis:</em> performance of a contract.
      </p>

      <h3>Payment data</h3>
      <p>
        Handled by Stripe (our payment processor). We receive only the limited transaction metadata
        Stripe shares with us (customer id, plan id, payment status, last 4 digits of card if
        applicable). We do NOT store your card number.<br />
        <em>Purpose:</em> to take payment and reconcile your subscription. <em>Lawful basis:</em> performance of a contract.
      </p>

      <h3>Communication data</h3>
      <p>
        Any emails or replies you send us, the contents of contact-form submissions, and our
        responses. Personal email correspondence is handled via our Protonmail account for
        end-to-end encryption.<br />
        <em>Purpose:</em> customer support, record-keeping, and to establish, pursue or defend legal
        claims. <em>Lawful basis:</em> our legitimate interest in operating the business.
      </p>

      <h3>Technical data</h3>
      <p>
        Your IP address, browser type, the pages you visit on our site, time-zone settings and
        similar device data. We use <strong>Cloudflare Web Analytics</strong> on the ProfitMIND
        marketing website — a privacy-respecting, cookieless analytics tool that aggregates visitor
        counts, top pages and referrers WITHOUT collecting personal data, setting cookies, or
        tracking you across sites. Inside the forecastly application we do NOT currently run any
        analytics; basic technical logs are kept by our hosting provider (Cloudflare) for security
        and abuse-prevention.<br />
        <em>Purpose:</em> to keep the service running and secure, and to understand which pages our
        visitors find useful. <em>Lawful basis:</em> our legitimate interest.
      </p>

      <h3>Marketing data</h3>
      <p>
        If you have opted in via the website or by becoming a customer, we may keep your email
        address in our MailerLite list to send you product updates and occasional helpful content.
        You can unsubscribe with one click in any email.<br />
        <em>Lawful basis:</em> consent (for marketing-only opt-ins) or our legitimate interest (for
        product updates to existing customers).
      </p>

      <h3>Sensitive data</h3>
      <p>
        We do NOT collect or process any sensitive personal data (race, ethnicity, religion, sexual
        orientation, political opinions, health, biometric data or criminal records).
      </p>

      <h3>Automated decision making and AI-generated content</h3>
      <p>
        We use AI (Anthropic's Claude model, accessed via Emergent Integrations) in two places:
      </p>
      <ul>
        <li>Inside <strong>forecastly</strong> — to generate narrative insights from your forecast and actuals.</li>
        <li>On the <strong>ProfitMIND Profit &amp; Cashflow Clarity Check</strong> — to generate your personalised Clarity Check report (the headline, summary, strengths, blind spots, recommended next steps and outlook) based on the answers you submit. Only the scoring numbers and your Clarity Check responses are sent at the moment of generation; no other personal identifiers are shared with the AI provider.</li>
      </ul>
      <p>
        These outputs are suggestions and educational insights — they do not produce any legal or
        financial decision that meaningfully affects you. You always remain in control, and you can
        email us at hello@profitmind.co.uk if you'd like a human review of anything we've produced.
      </p>

      <h2>3. How we collect your data</h2>
      <p><strong>Directly from you</strong> — when you fill out the website contact form, book a call via TidyCal, take the Profit &amp; Cashflow Clarity Check, sign up for forecastly, fill out your onboarding wizard, enter actuals, set goals, or contact us by email.</p>
      <p><strong>Automatically</strong> — basic technical data (IP, browser) when you load the site, and authentication cookies set when you log in to forecastly (see our <a href="/cookie-policy">Cookie Policy</a>).</p>
      <p><strong>From third parties</strong> we use to run the service:</p>
      <ul>
        <li><strong>TidyCal</strong> (calendar booking) — your booking details when you schedule a call with us.</li>
        <li><strong>Web3Forms</strong> (contact-form delivery) — when you submit the ProfitMIND website contact form, your name, email and message are sent through Web3Forms to our inbox. Web3Forms acts as a processor and does not retain submissions long-term.</li>
        <li><strong>Stripe</strong> (payments) — transaction metadata when you pay or subscribe.</li>
        <li><strong>MailerLite</strong> (marketing emails) — subscription status and engagement data if you receive our emails.</li>
        <li><strong>Resend</strong> (transactional emails) — delivery status for emails we send you.</li>
      </ul>

      <h2>4. Marketing communications</h2>
      <p>
        We will only send you marketing emails if you have explicitly opted in OR you are an
        existing customer and we have a legitimate interest in keeping you informed about product
        changes (under the Privacy and Electronic Communications Regulations). You can unsubscribe
        at any time using the link in every email, or by emailing <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>.
      </p>
      <p>
        Opting out of marketing does NOT affect transactional emails (password resets, payment
        receipts, account-deletion confirmations, monthly nudges to log your actuals, booking
        confirmations).
      </p>
      <p>We do not share your personal data with third parties for their own marketing purposes without your express consent.</p>

      <h2>5. Who we share your data with</h2>
      <p>We share your data with the following categories of service provider, strictly for the purpose of running the service:</p>
      <ul>
        <li><strong>Cloudflare</strong> (Cloudflare, Inc.) — website hosting, CDN, security and bot mitigation. We also use <strong>Cloudflare Web Analytics</strong> on the ProfitMIND marketing site to understand which pages are visited — no cookies, no personal data, fully aggregated.</li>
        <li><strong>TidyCal</strong> — calendar booking and scheduling.</li>
        <li><strong>Web3Forms</strong> (Web3Forms.com) — secure contact-form delivery for the ProfitMIND website. Forwards your submission to our inbox; does not store or use it for any other purpose.</li>
        <li><strong>Stripe</strong> (Stripe Payments Europe Ltd) — payment processing.</li>
        <li><strong>MailerLite</strong> (UAB MailerLite, Lithuania / MailerLite Inc., US) — marketing email delivery.</li>
        <li><strong>Resend</strong> (Resend.com Inc., US) — transactional email delivery.</li>
        <li><strong>Protonmail</strong> (Proton AG, Switzerland) — encrypted email correspondence.</li>
        <li><strong>Anthropic</strong> (via Emergent Integrations) — AI insight generation inside forecastly and on the ProfitMIND Profit &amp; Cashflow Clarity Check. Only the relevant numbers and your submitted answers are sent at the moment of generation. No personal account identifiers are shared.</li>
        <li><strong>MongoDB Atlas</strong> — encrypted database hosting (EU region) for forecastly.</li>
        <li><strong>Emergent</strong> — application hosting infrastructure.</li>
        <li>Our professional advisers (accountants, lawyers, insurers) when strictly necessary.</li>
        <li>Government bodies where we are required by law to disclose data.</li>
      </ul>
      <p>
        Each of these providers is bound by contract and by data protection law to keep your data
        secure and use it only for the purposes we instruct. We may also share your data if we sell,
        transfer or merge parts of our business or our assets. We will require any acquirer to
        honour this privacy notice.
      </p>

      <h2>6. International transfers</h2>
      <p>
        Some of our service providers are located outside the UK (notably Cloudflare, Anthropic,
        MailerLite Inc., Resend in the US, and Protonmail in Switzerland). Where we transfer your
        personal data outside the UK, we only do so to countries or providers whose data protection
        standards meet the UK's "not materially lower" test introduced by the Data (Use and Access)
        Act 2025, and we rely on UK-approved Standard Contractual Clauses, the UK-US Data Bridge,
        or equivalent safeguards to ensure your data has appropriate legal protection wherever it
        is processed.
      </p>

      <h2>7. Data security</h2>
      <p>
        We have implemented technical and organisational measures to protect your data — including
        bcrypt password hashing, HTTPS everywhere, encrypted database connections, access controls
        limited to authorised personnel, and a documented incident response procedure. We will
        notify you and the ICO of any personal data breach where we are required by law.
      </p>

      <h2>8. Data retention</h2>
      <p>We keep your data only for as long as we need it:</p>
      <ul>
        <li>Enquiry data, contact-form submissions and Clarity Check responses: 3 years from your last interaction.</li>
        <li>forecastly account, forecast, actuals and goals data: while your account is active. When you delete your account from inside the app, we permanently erase this data within 24 hours.</li>
        <li>Payment records: 6 years from the last transaction, to comply with HMRC tax record-keeping rules.</li>
        <li>Marketing list: until you unsubscribe.</li>
        <li>Support emails: 3 years from the last response.</li>
      </ul>
      <p>When we no longer need your personal data we either delete it or anonymise it for research/statistics.</p>

      <h2>9. Your legal rights</h2>
      <p>Under UK GDPR you have the right to:</p>
      <ul>
        <li><strong>Request access</strong> to your personal data ("subject access request").</li>
        <li><strong>Request correction</strong> of any data we hold that is wrong or incomplete.</li>
        <li><strong>Request erasure</strong> of your data (the "right to be forgotten").</li>
        <li><strong>Object</strong> to processing where we rely on legitimate interests.</li>
        <li><strong>Request restriction</strong> of processing in specific circumstances.</li>
        <li><strong>Request portability</strong> — receive your data in a structured, commonly used, machine-readable format.</li>
        <li><strong>Withdraw consent</strong> at any time where consent is the lawful basis.</li>
      </ul>
      <p>
        forecastly customers can exercise the right to portability and the right to erasure
        DIRECTLY from inside the app — profile → "Export my data" or "Delete my account". For any
        other request, please email <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>.
      </p>
      <p>
        We respond to all legitimate requests within one month. Where you make a subject access
        request, our searches will be reasonable and proportionate to the request, rather than
        exhaustive. There is no fee unless the request is clearly unfounded, repetitive or
        excessive.
      </p>
      <p>
        <strong>Complaining directly to us.</strong> You also have the right to raise a data
        protection complaint directly with us under section 164A of the Data Protection Act 2018.
        To do so, please email{" "}
        <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a> with "Data protection complaint" in the
        subject line. We will acknowledge your complaint within 30 days of receipt and respond
        fully without undue delay. If you remain unhappy after our response, you can escalate the
        matter to the Information Commissioner's Office (ICO), the UK's data protection regulator —{" "}
        <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">www.ico.org.uk</a>.
        We would appreciate the chance to address the issue with you first.
      </p>

      <h2>10. Third-party links</h2>
      <p>
        Our website and emails may include links to third-party websites and tools. We are not
        responsible for their privacy practices. When you leave our site, please read the privacy
        notice of every site you visit.
      </p>

      <h2>11. Children</h2>
      <p>
        ProfitMIND services and forecastly are intended for use by businesses and self-employed
        adults. We do not knowingly collect data about anyone under the age of 18.
      </p>

      <h2>12. Changes to this notice</h2>
      <p>
        We may update this privacy notice from time to time. The "Last updated" date at the top of
        the page reflects the most recent change. Significant changes will be notified to active
        customers by email.
      </p>
    </LegalLayout>
  );
}
